import { useEffect, useRef, useState } from 'react'
import { SITE,ROUTES } from '@/constants'
import Breadcrumb from '@common/Breadcrumb'
import { useRecaptcha } from '@helpers/useRecaptcha'
import { MAIL_API_URL, buildCareerEmailBody, readEnquiryStatus } from '@/helpers/sendMail'
import MetaInfo from '@common/MetaInfo'
import { metaTitleFor } from '../assets/js/navBreadcrumb'
const { label, pageDesc, metaDesc } = metaTitleFor(ROUTES.APPLY_FOR_JOB)

const HIRING_STEPS = [
  { number: '01', title: 'Apply Online', desc: 'Submit your resume and application through this page. We review every application personally.' },
  { number: '02', title: 'HR Screening',             desc: 'A brief call to understand your background, expectations, and interest in the role.' },
  { number: '03', title: 'Technical Assessment',  desc: 'A practical coding or design challenge tailored to the role — usually 60–90 minutes to assess your skills.' },
  { number: '04', title: 'Final Interview',     desc: 'Meet the hiring manager. Discuss your work profile, your salary, and your future here.' },
  { number: '05', title: 'Offer & Onboarding',  desc: 'If selected, you receive your offer letter and begin your journey with CruxBytes.' },
]

/* ── Form constants ── */
const APPLYING_FOR_OPTIONS = [
  'Web Designer / UI-UX Designer',
  'Web Developer (Full Stack / Back End / Front End)',
  'Mobile App Developer',
  'IT Internship (After Graduation)',
  'Project Manager / Team Lead',
  'Business Analyst / Domain Experts',
  'IT Sales/Pre-Sales',
  'Business Development Executive',
  'Internship In Sales',
  'Digital Marketing Executive',
  'Internship In Marketing',
]

const INIT = { name: '', email: '', mobile: '', applyingFor: '', heardAbout: '', resumeUrl: '' }

function validate(form) {
  const errors = {}
  if (!form.name.trim())   errors.name        = 'Name is required'
  if (!form.mobile.trim()) errors.mobile      = 'Mobile number is required'
 
  if (!form.email.trim())  errors.email       = 'Email is required'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                           errors.email       = 'Invalid email address'
  if (!form.applyingFor)   errors.applyingFor = 'Please select a role'
  return errors
}

const MAIL_SUBJECT = 'Job Enquiry'
/* ── Page ── */
export default function JobApplication() {
  const [form,           setForm          ] = useState(INIT)
  const [resumeFile,     setResumeFile    ] = useState(null)
  const [captchaToken,   setCaptchaToken  ] = useState('')
  const [pendingSubmit,  setPendingSubmit ] = useState(false)
  const [errors,         setErrors        ] = useState({})
  const [status,         setStatus        ] = useState('idle')
   useEffect(() => {
  const result = readEnquiryStatus()
  if (result) setStatus(result)
}, [])

  const formRef = useRef(null)
  const { execute: executeRecaptcha } = useRecaptcha()

  const handleChange = e => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }))
  }
  const handleFileChange = e => {
    setResumeFile(e.target.files?.[0] || null)
    if (errors.resume) setErrors(er => ({ ...er, resume: '' }))
  }
// v3 runs invisibly with no widget/challenge — execute() resolves directly
// with a token. Submission waits for it before the native POST fires
// (fetch can't be used here: no CORS on the mail-sending API).
const handleSubmit = async (e) => {
  e.preventDefault()

  const errs = validate(form)
  if (Object.keys(errs).length) {
    setErrors(errs)
    return
  }
  setStatus('loading')
  try {
    const token = await executeRecaptcha('career_application')
    setCaptchaToken(token)
    setPendingSubmit(true)
  } catch {
    setStatus('idle')
    setErrors(er => ({ ...er, recaptcha: 'Verification failed. Please try again.' }))
  }
}

useEffect(() => {
  if (pendingSubmit && captchaToken) {
    setPendingSubmit(false)
    formRef.current?.submit()
  }
}, [pendingSubmit, captchaToken])

  return (
    <>    
     <MetaInfo
     title={label} 
      description={metaDesc}
      keywords=""
      />
    <main id="main-content" className="page-wrapper">

      {/* ── Hero ── */}
      <div className="page-hero page-hero-small">
        <div className="bg-grid bg-grid--fill" aria-hidden="true" />
        <div className="container">
          {/* <div className="section-label section-label-small">- Careers -</div> */}
            <Breadcrumb />
          <h1 data-aos="fade-up" data-aos-delay={100}>
            {/* Apply for Job */}
            {label}
            </h1>
          <p className="page-subtitle page-subtitle-small" data-aos="fade-up" data-aos-delay={200}>
            {/* Unlock your potential, explore challenging opportunities and make yourself shine amongst the growing technological challenges. Come, join our team and feel the difference. */}
            {pageDesc}
          </p>
                    
        </div>
      </div>

      {/* ── Main: infographic + form ── */}
      <div className="section-py bg-linear-gradient-light">
        <div className="container career-split">

          {/* ── LEFT: hiring process infographic ── */}
 <div className="cp-timeline" data-aos="fade-right" aria-label="Our hiring process">

  <div className="cp-timeline-header">
    <h2 >Our Hiring Process</h2>
    <p >
     A few straightforward steps from application to your first day.
    </p>
  </div>

 <div className="cp-vline-track" aria-label="Hiring process steps">

  {/* Vertical line — purely decorative */}
  <div className="cp-vline" aria-hidden="true" />

  {HIRING_STEPS.map((step, idx) => (
    <div
      key={step.number}
      className="cp-vline-step"
      data-aos="fade-up"
      data-aos-delay={idx * 80}
    >
      {/* Numbered circle badge */}
     {/* Diamond badge — number via data-number, counter-rotated in CSS */}
<div
  className="cp-vline-badge"
  data-number={step.number}
  aria-hidden="true"
/>

      {/* Title + description */}
      <div className="cp-vline-content">
        <h3 className="cp-vline-title">{step.title}</h3>
        <p className="cp-vline-desc">{step.desc}</p>
      </div>
    </div>
  ))}

</div>

</div>

          {/* ── RIGHT: application form ── */}
          <div id="apply-form" className="cp-form-wrap" data-aos="fade-left">
            <div className="contact-form-wrap career-form-wrap">
                <p style={{paddingBottom:'1.2rem'}}>
                  <b>Submit your CV, we will contact you as soon as we have relevant openings</b></p>
              {status === 'success' ? (
                <div className="contact-success" role="alert">
                  <i className="fa-solid fa-circle-check" />
                  <h3>Application Received!</h3>
                  <p>Thank you — our team will review your details and get back to you shortly.</p>                  
                </div>
              ) : (
                <form
  ref={formRef}
  onSubmit={handleSubmit}
  action={MAIL_API_URL}
  method="POST"
   
  className="contact-form"
  noValidate
  aria-label="Job application form"
>
  {/* Hidden fields */}
  <input type="hidden" name="EmailTo"   value={form.email} />
  <input type="hidden" name="EmailBcc"  value={SITE.mail_bcc} />
  {/* <input type="hidden" name="Subject"   value={MAIL_SUBJECT} /> */}
  <input type="hidden" name="Subject"   value={form.applyingFor ? `${MAIL_SUBJECT} | ${form.applyingFor}` : MAIL_SUBJECT} />
  <input type="hidden" name="EmailBody" value={buildCareerEmailBody(form)} />
  <input type="hidden" name="name"      value={form.name} />
  <input type="hidden" name="email"     value={form.email} />
  <input type="hidden" name="contact"   value={form.mobile} />
  <input type="hidden" name="ReturnUrl" value={ROUTES.APPLY_FOR_JOB} />
  <input type="hidden" name="filename" value={form.resumeUrl} />   
  <input type="hidden" name="source" value="apply-for-job" />
  <input type="hidden" name="EmailAlias"   value={SITE.emailAlias} />
<input type="hidden" name="eMailSenderIDFrom"   value={SITE.eMailSenderIDFrom} />
   <div className="form-field">
                    <label htmlFor="applyingFor" className="form-label">
                      Applying For <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    <select id="applyingFor" name="applyingFor"
                      value={form.applyingFor} onChange={handleChange}
                      className={`form-input${errors.applyingFor ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.applyingFor}>
                      <option value="">-- Select --</option>
                      {APPLYING_FOR_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {errors.applyingFor && <span role="alert" className="form-err-msg">{errors.applyingFor}</span>}
                  </div>
                  <div className="form-field">
                    <label htmlFor="name" className="form-label">
                      Your Name <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    <input id="name" name="name" type="text" placeholder="Your full name"
                      value={form.name} onChange={handleChange}
                      className={`form-input${errors.name ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'err-name' : undefined}
                      autoComplete="name" />
                    {errors.name && <span id="err-name" role="alert" className="form-err-msg">{errors.name}</span>}
                  </div>

                  <div className="form-field">
                    <label htmlFor="email" className="form-label">Email Id <span className="form-req" aria-hidden="true">*</span></label>
                    <input id="email" name="email" type="email" placeholder="you@example.com"
                      value={form.email} onChange={handleChange}
                      className={`form-input${errors.email ? ' form-input-error' : ''}`}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'err-email' : undefined}
                      autoComplete="email" />
                    {errors.email && <span id="err-email" role="alert" className="form-err-msg">{errors.email}</span>}
                  </div>

                  <div className="form-field">
                    <label htmlFor="mobile" className="form-label">
                      Mobile Number <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    <input id="mobile" name="mobile" type="tel" placeholder="+91 XXXXX XXXXX"
                      value={form.mobile} onChange={handleChange}
                      className={`form-input${errors.mobile ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.mobile}
                      aria-describedby={errors.mobile ? 'err-mobile' : undefined}
                      autoComplete="tel" />
                    {errors.mobile && <span id="err-mobile" role="alert" className="form-err-msg">{errors.mobile}</span>}
                  </div>

               

                  {/* <div className="form-field">
                    <label htmlFor="resumeUrl" className="form-label">
                      Upload Resume (.pdf / .docx) <span className="form-req" aria-hidden="true">*</span>
                    </label>
                    <input id="resumeUrl" name="resumeUrl" type="file" accept=".pdf,.docx"
                      onChange={handleFileChange}
                      className={`form-input${errors.resumeUrl ? ' form-input-error' : ''}`}
                      aria-required="true" aria-invalid={!!errors.resume}
                      aria-describedby={errors.resume ? 'err-resume' : undefined} />
                    {errors.resumeUrl && <span id="err-resume" role="alert" className="form-err-msg">{errors.resumeUrl}</span>}
          
                  </div> */}
                    <div className="form-field">
                    <label htmlFor="resumeUrl" className="form-label">
                      Resume Link (pdf / docx)
                    </label>
                     <input
    id="resumeUrl"
    name="resumeUrl"
    type="url"
    placeholder="Paste your Resume link"
    value={form.resumeUrl}
    onChange={handleChange}
    className={`form-input${errors.resumeUrl ? ' form-input-error' : ''}`}
    aria-required="true"
  />
  {errors.resumeUrl && <span role="alert" className="form-err-msg">{errors.resumeUrl}</span>} 
    </div>

                  {/* <div className="form-field">
                    <label htmlFor="heardAbout" className="form-label">How Did You Hear About Us</label>
                    <select id="heardAbout" name="heardAbout"
                      value={form.heardAbout} onChange={handleChange}
                      className="form-input">
                      <option value="">-- Select --</option>
                      {HEARD_ABOUT_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div> */}

                                   {/* reCAPTCHA v3 — no visible widget; token travels via hidden field */}
                  <input type="hidden" name="g-recaptcha-response" value={captchaToken} />
                  {errors.recaptcha && (
                    <div className="form-field">
                      <span role="alert" className="form-err-msg">{errors.recaptcha}</span>
                    </div>
                  )}
{status === 'error' && (
  <p role="alert" aria-live="assertive" className="form-status form-status-error">
    Sorry, we couldn't send your application. Please try again or contact us directly.
  </p>
)}
                  <button
                    type="submit"
                    className="cb-btn cb-btn-primary cb-btn-md"                    
                    // disabled
                    style={{width:'15rem'}}
                  >
                    {status === 'loading' ? 'Submitting…' : 'Submit Application'}
                  </button>
                  {/* <p>Currently unavailable</p> */}
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </main>
    </>
  )
}